import { z } from "zod";
import { getTranslatedPath } from "@/utils/routes";

export const JOB_TYPES = ["gtm", "engineering", "cx"] as const;
export type JobType = (typeof JOB_TYPES)[number];

export const jobTypeSchema = z.enum(JOB_TYPES);

export const jobListingSchema = z.object({
  id: z.string().min(1),
  type: jobTypeSchema,
  title: z.string().min(1),
  summary: z.string().min(1),
  description: z.string().min(1),
  location: z.string().optional(),
  compensation: z.string().optional(),
  employmentType: z
    .enum(["full-time", "part-time", "contract", "internship"])
    .optional(),
  departmentLabel: z.string().optional(),
  applyEnabled: z.boolean().optional(),
  order: z.number().optional(),
  status: z.enum(["open", "closed", "draft"]),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type JobListing = z.infer<typeof jobListingSchema>;

export const MAX_JOBS_PER_VIEW = 9;

export const JOB_KEY_PREFIX = "jobs_";

/** Redis key for a single job posting: `jobs_{type}_{id}` */
export function jobListingRedisKey(type: JobType, id: string): string {
  return `${JOB_KEY_PREFIX}${type}_${id}`;
}

export function jobParam(job: Pick<JobListing, "type" | "id">): string {
  return `${job.type}:${job.id}`;
}

/**
 * Localized path to a single job posting page, including the language prefix
 * (e.g. `/es/carreras/engineering/senior-ai-engineer`).
 */
export function jobDetailPath(
  job: Pick<JobListing, "type" | "id">,
  lang: string,
): string {
  const careersPath = getTranslatedPath(`/careers/${job.type}`, lang);
  return `/${lang}${careersPath}/${encodeURIComponent(job.id)}`;
}

export function parseJobParam(
  value: string | null | undefined,
): { type: JobType; id: string } | null {
  if (!value) return null;
  const [type, id, ...extra] = value.split(":");
  if (extra.length > 0 || !type || !id || !isJobType(type)) return null;
  return { type, id };
}

export function parseJobListingRedisKey(
  key: string,
): { type: JobType; id: string } | null {
  if (!key.startsWith(JOB_KEY_PREFIX)) return null;
  const rest = key.slice(JOB_KEY_PREFIX.length);
  for (const type of JOB_TYPES) {
    const prefix = `${type}_`;
    if (rest.startsWith(prefix)) {
      const id = rest.slice(prefix.length);
      if (id.length > 0) return { type, id };
    }
  }
  return null;
}

export function jobOgImagePath(job: Pick<JobListing, "type" | "id">): string {
  return `/api/jobs/og/${job.type}/${encodeURIComponent(job.id)}`;
}

export function jobOgCacheKey(job: Pick<JobListing, "type" | "id" | "updatedAt">): string {
  return `job_og_${job.type}_${job.id}_${job.updatedAt}`;
}

export function isJobType(value: string): value is JobType {
  return (JOB_TYPES as readonly string[]).includes(value);
}

export function jobTypeLabel(type: JobType): string {
  switch (type) {
    case "gtm":
      return "GTM";
    case "engineering":
      return "ENGINEERING";
    case "cx":
      return "CX";
    default:
      return type;
  }
}
